import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import ReactMarkdown from 'react-markdown';
import { ScreeningResult } from '../api/screening.api';

interface ResultsTableProps {
  result: ScreeningResult;
}

export default function ResultsTable({ result }: ResultsTableProps) {
  const { finalOutput, evaluations, csvContent, candidateCount, agentName } =
    result;

  // Split output into summary (before "# Detailed Evaluations") and details
  const detailIdx = finalOutput.indexOf('# Detailed Evaluations');
  const summary = detailIdx > 0 ? finalOutput.slice(0, detailIdx) : finalOutput;
  const details = detailIdx > 0 ? finalOutput.slice(detailIdx) : null;

  // Parse individual candidate sections from details
  const candidates: { name: string; content: string }[] = [];
  if (details) {
    const sections = details.split(/\n## /).slice(1);
    for (const section of sections) {
      const newlineIdx = section.indexOf('\n');
      if (newlineIdx > 0) {
        candidates.push({
          name: section.slice(0, newlineIdx).trim(),
          content: section.slice(newlineIdx).trim(),
        });
      }
    }
  }

  const downloadCsv = () => {
    if (!csvContent) return;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'priority_ranking.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadMarkdown = () => {
    const blob = new Blob([finalOutput], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'screening_results.md';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Alert severity="success" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Screening Complete — {candidateCount} candidate
          {candidateCount !== 1 ? 's' : ''} ranked
        </Typography>
        <Typography variant="body2">Agent: {agentName}</Typography>
      </Alert>

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={downloadMarkdown}
          size="small"
        >
          Download Report
        </Button>
        {csvContent && (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={downloadCsv}
            size="small"
          >
            Download CSV
          </Button>
        )}
      </Box>

      {/* Priority Ranking Summary */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <ReactMarkdown
          components={{
            table: ({ children }) => (
              <Box sx={{ overflowX: 'auto', mb: 2 }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.875rem',
                  }}
                >
                  {children}
                </table>
              </Box>
            ),
            th: ({ children }) => (
              <th
                style={{
                  textAlign: 'left',
                  padding: '10px 14px',
                  borderBottom: '2px solid #e0e0e0',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  letterSpacing: '0.05em',
                  backgroundColor: '#f5f5f5',
                }}
              >
                {children}
              </th>
            ),
            td: ({ children }) => {
              const text = String(children || '');
              let chipColor: 'success' | 'warning' | 'default' = 'default';
              if (text.includes('PRIORITY REVIEW')) chipColor = 'success';
              else if (text.includes('FULL REVIEW')) chipColor = 'warning';

              if (
                text.includes('PRIORITY REVIEW') ||
                text.includes('FULL REVIEW') ||
                text.includes('STANDARD REVIEW')
              ) {
                return (
                  <td style={{ padding: '10px 14px', borderBottom: '1px solid #eee' }}>
                    <Chip label={text.trim()} color={chipColor} size="small" />
                  </td>
                );
              }
              return (
                <td style={{ padding: '10px 14px', borderBottom: '1px solid #eee' }}>
                  {children}
                </td>
              );
            },
          }}
        >
          {summary}
        </ReactMarkdown>
      </Paper>

      {/* Detailed Evaluations */}
      {candidates.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Detailed Candidate Evaluations
          </Typography>
          {candidates.map((c, i) => (
            <Accordion key={i}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={500}>{c.name}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ReactMarkdown>{c.content}</ReactMarkdown>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Box>
  );
}
